import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Leaf, HeartHandshake, Sprout, Truck, MapPin, Phone, Handshake } from "lucide-react";
import { api } from "../lib/api";
import FeaturedProducts from "../components/FeaturedProducts";

const HERO_IMG_FALLBACK =
  "https://images.unsplash.com/photo-1664339307400-9c22e5f44496?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxmYXJtJTIwZnJlc2glMjBicm93biUyMGVnZ3N8ZW58MHx8fHwxNzc4Mjg4Njg5fDA&ixlib=rb-4.1.0&q=85";

const HIGHLIGHTS = [
  { icon: Sprout, title: "Farm Fresh Everyday", body: "Hand-collected from NZ farms in the morning, on your bench by the afternoon." },
  { icon: ShieldCheck, title: "Quality You Can Trust", body: "We only partner with growers who hand-grade for size, shell strength, and care." },
  { icon: Leaf, title: "Rich in Nutrition", body: "Healthier hens means richer yolks and better eggs for your whānau." },
  { icon: HeartHandshake, title: "Made with Care", body: "We're not the farm — we're the bridge. Fair to growers, fair to you." },
];

// Real, honest launch stat — first delivery on 8 May 2026.
const LAUNCH_DATE = new Date("2026-05-08");
const CARTONS_DELIVERED = 5000;

const BANNER_TONES = {
  green: "bg-[#166534] text-white",
  orange: "bg-[#EA580C] text-white",
  yellow: "bg-[#F4C430] text-stone-900",
  dark: "bg-[#0E2818] text-white",
};

const PROMO_TONES = {
  light: "bg-white text-stone-900 border border-stone-200",
  green: "bg-[#166534] text-white",
  orange: "bg-[#EA580C] text-white",
  dark: "bg-[#0E2818] text-white",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [cms, setCms] = useState(null);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/cms").then((r) => setCms(r.data)).catch(() => {});
  }, []);
  const featured = products.slice(0, 3);

  const daysSinceLaunch = Math.max(1, Math.floor((Date.now() - LAUNCH_DATE.getTime()) / 86400000));

  const hero = cms?.hero || {};
  const story = cms?.story || {};
  const prodSec = cms?.product_section || {};
  const cta = cms?.cta || {};
  const banners = (cms?.banners || []).filter((b) => b && b.active && b.text);
  const promos = (cms?.promo_sections || []).filter((p) => p && p.active);

  return (
    <div data-testid="home-page">
      {/* Top admin-editable banners */}
      {banners.length > 0 && (
        <div className="flex flex-col">
          {banners.map((b, i) => {
            const inner = (
              <div className={`text-center px-6 py-2.5 text-sm font-semibold ${BANNER_TONES[b.tone] || BANNER_TONES.green}`} data-testid={`cms-banner-${i}`}>
                {b.text}
              </div>
            );
            return b.link ? (
              <a key={i} href={b.link} className="block hover:opacity-90">{inner}</a>
            ) : (
              <div key={i}>{inner}</div>
            );
          })}
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAFAF9]">
        {/* Slim announcement bar with Suggest CTA */}
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-12 pt-4 sm:pt-6">
          <Link
            to="/suggest"
            data-testid="home-suggest-cta"
            className="inline-flex items-center gap-2 rounded-full bg-white border-2 border-[#EA580C]/20 hover:border-[#EA580C] px-4 py-1.5 text-[11px] sm:text-xs font-bold text-stone-900 transition group"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#EA580C]" />
            <span className="uppercase tracking-widest text-[#EA580C]">New</span>
            <span className="text-stone-700 group-hover:text-stone-900">Suggest a product you&apos;d love us to stock →</span>
          </Link>
        </div>

        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-12 pt-6 sm:pt-8 md:pt-14 pb-14 sm:pb-20 md:pb-28 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 border border-green-200">
              <span className="h-2 w-2 rounded-full bg-[#16A34A]" />
              <span className="text-[11px] font-semibold uppercase tracking-widest text-[#166534]" data-testid="hero-badge">
                {hero.badge || "Now delivering · Auckland only — more areas coming soon"}
              </span>
            </div>
            <h1 className="font-display font-black tracking-tight text-stone-900 mt-5 sm:mt-6 text-[2.5rem] leading-[1] sm:text-6xl lg:text-7xl sm:leading-[0.95]" data-testid="hero-title">
              {hero.title_pre || "Fresh eggs"} <span className="text-[#EA580C] italic">{hero.title_accent || "delivered"}</span>
              <br />
              {hero.title_line2_pre || "to your"} <span className="underline decoration-[#166534] decoration-[5px] sm:decoration-[6px] underline-offset-[6px] sm:underline-offset-[8px]">{hero.title_underline || "door."}</span>
            </h1>
            <p className="mt-5 sm:mt-6 text-stone-600 text-base sm:text-lg max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {hero.subtitle || "EGGMAN is the bridge between New Zealand farms and Kiwi families."}
            </p>
            <div className="mt-7 sm:mt-9 flex flex-wrap gap-2.5 sm:gap-3">
              <Link
                to={hero.primary_cta_link || "/products"}
                data-testid="hero-order-now-btn"
                className="bg-[#EA580C] text-white rounded-full font-bold px-6 sm:px-8 py-3.5 sm:py-4 hover:bg-[#c2410c] transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                {hero.primary_cta_text || "Order Now"} <ArrowRight size={18} />
              </Link>
              <Link
                to={hero.secondary_cta_link || "/about"}
                data-testid="hero-about-btn"
                className="border-2 border-stone-200 text-stone-900 rounded-full font-semibold px-6 sm:px-8 py-3.5 sm:py-4 hover:border-[#166534] transition-colors"
              >
                {hero.secondary_cta_text || "Our Story"}
              </Link>
              {hero.phone_label && (
                <a
                  href={hero.phone_link || "tel:0223734083"}
                  data-testid="hero-call-btn"
                  className="hidden sm:flex border-2 border-stone-200 text-stone-900 rounded-full font-semibold px-6 py-4 hover:border-[#EA580C] transition-colors items-center gap-2"
                >
                  <Phone size={16} /> {hero.phone_label}
                </a>
              )}
            </div>
            <div className="mt-10 flex items-center gap-7 text-sm flex-wrap">
              <div className="flex items-center gap-2 text-stone-600">
                <Truck size={16} className="text-[#166534]" />
                <span>{hero.stat_label || `${CARTONS_DELIVERED} cartons delivered & growing`}</span>
              </div>
              <div className="flex items-center gap-2 text-stone-600">
                <Handshake size={16} className="text-[#EA580C]" />
                <span>Connecting farms &amp; consumers</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-[1.75rem] sm:rounded-[2rem] overflow-hidden border-4 sm:border-8 border-white shadow-2xl">
              <img src={hero.image_url || HERO_IMG_FALLBACK} alt="Basket of farm fresh brown eggs" className="w-full h-[320px] sm:h-[420px] md:h-[480px] object-cover" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-[#166534] flex items-center justify-center text-white">
                  <Sprout size={18} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">This week</div>
                  <div className="font-display font-bold text-stone-900">{hero.image_caption || "Free Range · Size 7"}</div>
                </div>
                <div className="ml-auto font-display font-black text-2xl text-[#EA580C]">{hero.image_price || "$13.50"}</div>
              </div>
            </div>
            <div className="hidden md:flex absolute -top-3 -left-6 bg-[#166534] text-white px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg rotate-[-4deg]">
              <span>NZ Owned · Supporting Local</span>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-stone-200 bg-white overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-12 px-12 font-display font-bold text-2xl text-stone-300">
                <span>FRESH EGGS</span><span className="text-[#EA580C]">●</span>
                <span>BETTER NUTRITION</span><span className="text-[#166534]">●</span>
                <span>TRUSTED QUALITY</span><span className="text-[#EA580C]">●</span>
                <span>SUPPORTING LOCAL FARMS</span><span className="text-[#166534]">●</span>
                <span>FAIR PRICE FOR ALL</span><span className="text-[#EA580C]">●</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS — now matches the reference brand strip */}
      <section className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-12 py-14 sm:py-20 md:py-28">
        <div className="max-w-2xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]">What we stand for</div>
          <h2 className="font-display font-black text-3xl sm:text-5xl text-stone-900 tracking-tight mt-3">
            Honest eggs. Fair prices. Local first.
          </h2>
        </div>
        <div className="mt-8 sm:mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {HIGHLIGHTS.map((h) => (
            <div
              key={h.title}
              data-testid={`highlight-${h.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-white rounded-2xl border border-stone-200 p-7 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
            >
              <div className="h-12 w-12 rounded-2xl bg-green-100 text-[#166534] flex items-center justify-center">
                <h.icon size={22} />
              </div>
              <h3 className="font-display font-bold text-xl text-stone-900 mt-5">{h.title}</h3>
              <p className="text-stone-600 mt-2 leading-relaxed text-sm">{h.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OUR STORY (admin-editable) */}
      <section className="bg-[#0E2818] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]" data-testid="story-eyebrow">{story.eyebrow || "Our story"}</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-3" data-testid="story-title">
              {story.title || "We're farm-to-door partners — not the farm."}
            </h2>
            <p className="text-stone-300 mt-5 leading-relaxed text-lg" data-testid="story-body1">
              {story.body1 || "EGGMAN started with a simple goal..."}
            </p>
            <p className="text-stone-300 mt-4 leading-relaxed" data-testid="story-body2">
              {story.body2 || ""}
            </p>
            <Link
              to="/about"
              className="mt-8 inline-flex items-center gap-2 text-[#EA580C] font-bold hover:underline"
              data-testid="our-story-link"
            >
              Read more <ArrowRight size={16} />
            </Link>
          </div>

          {/* Real numbers */}
          <div className="grid grid-cols-2 gap-4">
            <StatCard value={CARTONS_DELIVERED} label="Cartons delivered" sub="and counting" />
            <StatCard value={`Day ${daysSinceLaunch}`} label="Since 8 May 2026" sub="launch day" />
            <StatCard value="1" label="City live" sub="Auckland" />
            <StatCard value="∞" label="Future plans" sub="more groceries soon" />
          </div>
        </div>
      </section>

      {/* DELIVERY AREAS — matches reference design strip */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="bg-[#166534] text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
            <Truck size={40} className="text-[#EA580C]" />
            <div className="font-display font-black text-3xl mt-4">Delivery Available</div>
            <div className="text-[#F4C430] font-display font-black text-4xl mt-1 tracking-tight">Auckland</div>
            <p className="text-green-100 mt-4 text-sm leading-relaxed">
              We currently deliver only across Auckland — more areas coming soon.
            </p>
          </div>

          <div className="bg-white border border-stone-200 rounded-3xl p-8 md:p-10">
            <MapPin size={40} className="text-[#166534]" />
            <div className="font-display font-black text-3xl mt-4 text-stone-900">Out of these areas?</div>
            <div className="text-[#EA580C] font-display font-black text-2xl mt-1 tracking-tight">Bulk orders only.</div>
            <p className="text-stone-600 mt-4 text-sm leading-relaxed">
              Other locations across NZ are available for bulk orders. Give us a call to arrange.
            </p>
            <a
              href="tel:0223734083"
              className="mt-5 inline-flex items-center gap-2 text-[#166534] font-bold hover:underline"
            >
              <Phone size={14} /> 022 373 4083
            </a>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS (admin-pinned via CMS — renders nothing if none pinned) */}
      <FeaturedProducts scope="home" title="Featured this week" subtitle="Hand-picked by our team — limited time." />

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-2xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]">{prodSec.eyebrow || "Our products"}</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-stone-900 tracking-tight mt-3">
              {prodSec.title || "Two grades. Real quality."}
            </h2>
            <p className="text-stone-600 mt-3">{prodSec.subtitle || "Colony & Free Range — pick the one your kitchen calls for."}</p>
          </div>
          <Link
            to="/products"
            data-testid="see-all-products-link"
            className="text-sm font-bold text-[#166534] hover:text-[#14532d] flex items-center gap-1.5"
          >
            See all 6 products <ArrowRight size={16} />
          </Link>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p) => (
            <Link
              key={p.id}
              to="/products"
              data-testid={`featured-${p.id}`}
              className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 block"
            >
              <div className="h-56 bg-stone-100 overflow-hidden">
                {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
              </div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-widest text-[#166534] font-bold">{p.size_label || "Eggs"}</div>
                <div className="font-display font-bold text-xl mt-1 text-stone-900">{p.name}</div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="font-display font-black text-2xl text-[#EA580C]">${(p.price_excl_gst || 0).toFixed(2)}</div>
                  <span className="text-xs font-bold text-stone-500">excl. GST</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY FRESH MATTERS */}
      <section className="bg-[#FAFAF9] border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]">Why fresh matters</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl text-stone-900 tracking-tight mt-3">
                A fresh egg is a different egg.
              </h2>
              <p className="mt-5 text-stone-600 leading-relaxed">
                Most supermarket eggs are 2–4 weeks old by the time they hit your fridge.
                Ours are typically <b className="text-[#166534]">collected within 24–48 hours</b> of delivery.
                Fresher eggs mean firmer whites, brighter yolks, and visibly better baking.
              </p>
              <div className="mt-7 grid sm:grid-cols-2 gap-3">
                <FreshFact n="<48h" label="From hen to your door" />
                <FreshFact n="2× firmer" label="Whites that hold shape" />
                <FreshFact n="Brighter yolks" label="From pasture-fed hens" />
                <FreshFact n="Zero plastic" label="Recyclable cartons only" />
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 gap-3">
              <CompareCard
                tone="bad"
                title="Supermarket egg"
                pts={["2–4 weeks old", "Pale yolk", "Watery white", "Plastic + cardboard packaging"]}
              />
              <CompareCard
                tone="good"
                title="EGGMAN egg"
                pts={["Within 48 hours of you", "Vibrant orange yolk", "Firm, holds its shape", "Recycled paper carton only"]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]">Early customers</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl text-stone-900 tracking-tight mt-3">
              What our first 85 cartons taught us.
            </h2>
            <p className="text-stone-600 mt-3">A few real notes from people who let us into their kitchens.</p>
          </div>
          <div className="text-right">
            <div className="font-display font-black text-5xl text-[#166534]">{CARTONS_DELIVERED}</div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">cartons &amp; counting</div>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <Testimonial
            quote="Honestly the best yolks I've cooked in years. Bright orange, holds shape — exactly like nan's."
            name="Aroha M."
            sub="Auckland · Mt Eden"
          />
          <Testimonial
            quote="Cheaper than New World and the driver is friendly. We've already switched three of our neighbours."
            name="James W."
            sub="Auckland · Hillcrest"
          />
          <Testimonial
            quote="Love the no-fluff vibe. Honest people, honest eggs. Got a full carton + one for my mum."
            name="Maia N."
            sub="Auckland · Ponsonby"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#FAFAF9] border-y border-stone-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 py-20 md:py-24">
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#EA580C]">Common questions</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-stone-900 tracking-tight mt-3">
            Things people ask us.
          </h2>

          <div className="mt-10 space-y-3">
            <FAQ
              q="So you're not the farm?"
              a="Correct — we're not the farm. We're the bridge. EGGMAN partners with NZ farms directly and delivers their eggs to your door, cutting out supermarkets and warehousing in between."
            />
            <FAQ
              q="How fresh are the eggs?"
              a="Most cartons are with you within 24–48 hours of being collected from the farm. Compare that to 2–4 weeks for an average supermarket egg."
            />
            <FAQ
              q="Where do you deliver?"
              a="Auckland-wide. FREE delivery within 8.5 km of our hubs when you order 2 or more cartons. Single cartons or addresses beyond 8.5 km are a flat $3.50. Other parts of NZ — bulk orders only. Give us a call on 022 373 4083 to arrange."
            />
            <FAQ
              q="How do payments work?"
              a="Secure Stripe checkout in NZD. Your order confirmation lands in your inbox instantly. Cash/EFTPOS-on-delivery is also available."
            />
            <FAQ
              q="Are you adding more groceries?"
              a="That's the plan. Eggs are step one. We're going to add more daily essentials — connecting more local farms with more Kiwi households as we grow."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-16 md:py-20">
        <div className="rounded-3xl bg-[#EA580C] text-white p-10 md:p-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-display font-black text-4xl sm:text-5xl tracking-tight">
              {cta.title || "Thank you for supporting local."}
            </h3>
            <p className="text-orange-100 mt-4 text-lg max-w-md">
              {cta.subtitle || "Every carton you order helps a NZ farm reach more Kiwi tables."}
            </p>
          </div>
          <div className="flex md:justify-end">
            <Link
              to={cta.button_link || "/products"}
              data-testid="cta-order-btn"
              className="bg-white text-[#EA580C] rounded-full font-bold px-8 py-4 hover:bg-stone-100 transition-all shadow-md flex items-center gap-2"
            >
              {cta.button_text || "Order eggs"} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Admin-managed promo sections */}
      {promos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pb-16 md:pb-24 space-y-5">
          {promos.map((p, i) => (
            <div key={p.id || i} data-testid={`promo-section-${i}`} className={`rounded-3xl p-8 md:p-10 grid md:grid-cols-2 gap-8 items-center ${PROMO_TONES[p.tone] || PROMO_TONES.light}`}>
              <div>
                <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{p.title}</h3>
                {p.body && <p className="mt-3 opacity-90 max-w-md whitespace-pre-line">{p.body}</p>}
                {p.link && p.link_text && (
                  <Link to={p.link} className="mt-6 inline-flex items-center gap-2 bg-white/95 text-stone-900 rounded-full font-bold px-6 py-3 hover:bg-white">
                    {p.link_text} <ArrowRight size={16} />
                  </Link>
                )}
              </div>
              {p.image_url && (
                <div className="md:justify-self-end w-full">
                  <img src={p.image_url} alt={p.title} className="rounded-2xl w-full max-h-72 object-cover" />
                </div>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function StatCard({ value, label, sub }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
      <div className="font-display font-black text-3xl sm:text-4xl text-[#F4C430] tracking-tight">{value}</div>
      <div className="text-white font-bold text-sm mt-2">{label}</div>
      <div className="text-stone-400 text-xs mt-0.5">{sub}</div>
    </div>
  );
}

function FreshFact({ n, label }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3.5">
      <div className="font-display font-black text-xl text-[#166534]">{n}</div>
      <div className="text-stone-600 text-xs mt-1">{label}</div>
    </div>
  );
}

function CompareCard({ tone, title, pts }) {
  const isGood = tone === "good";
  return (
    <div className={`rounded-2xl p-6 border ${isGood ? "bg-[#166534] text-white border-[#166534]" : "bg-white text-stone-700 border-stone-200"}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${isGood ? "text-[#F4C430]" : "text-stone-500"}`}>
        {isGood ? "Better" : "Average"}
      </div>
      <div className={`font-display font-bold text-xl mt-1 ${isGood ? "text-white" : "text-stone-900"}`}>{title}</div>
      <ul className="mt-4 space-y-2 text-sm">
        {pts.map((p) => (
          <li key={p} className="flex items-start gap-2">
            <span className={isGood ? "text-[#F4C430]" : "text-stone-400"}>{isGood ? "✓" : "—"}</span>
            <span className={isGood ? "" : "text-stone-600"}>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Testimonial({ quote, name, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-7 hover:-translate-y-1 hover:shadow-lg transition-all">
      <div className="font-display text-5xl text-[#F4C430] leading-none">"</div>
      <p className="text-stone-700 mt-2 leading-relaxed">{quote}</p>
      <div className="mt-5 pt-5 border-t border-stone-100">
        <div className="font-bold text-stone-900">{name}</div>
        <div className="text-xs text-stone-500">{sub}</div>
      </div>
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-${q.toLowerCase().slice(0, 20).replace(/\W+/g, "-")}`}
        className="w-full text-left p-5 md:p-6 flex items-center justify-between gap-4 hover:bg-stone-50 transition-colors"
      >
        <span className="font-display font-bold text-stone-900 text-lg">{q}</span>
        <span className={`shrink-0 h-7 w-7 rounded-full bg-[#166534] text-white flex items-center justify-center transition-transform ${open ? "rotate-45" : ""}`}>
          +
        </span>
      </button>
      {open && (
        <div className="px-5 md:px-6 pb-6 text-stone-600 leading-relaxed -mt-1">{a}</div>
      )}
    </div>
  );
}
